import React, { useState, useEffect, useRef } from 'react';
import { motion, useInView, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import heroBg from './assets/hero-bg.jpg';
import resort1 from './assets/resort1.jpg';
import resort2 from './assets/resort2.jpg';
import resort3 from './assets/resort3.jpg';
import resort4 from './assets/resort4.jpg';
import resort5 from './assets/resort5.jpg';
import resort6 from './assets/resort6.jpg';
import resort7 from './assets/resort7.jpg';
import resort8 from './assets/resort8.jpg';

function FadeInSection({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <motion.div
      ref={ref}
      initial={{ y: 40, opacity: 0 }}
      animate={isInView ? { y: 0, opacity: 1 } : { y: 40, opacity: 0 }}
      transition={{ duration: 0.8, delay, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function App() {
  const [isLoaded, setIsLoaded] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(null);

  // Booking form state
  const [bookingForm, setBookingForm] = useState({
    checkIn: '',
    checkOut: '',
    guests: '2',
    room: 'deluxe',
  });

  useEffect(() => {
    setIsLoaded(true);
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
      setShowScrollTop(window.scrollY > 600);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const { scrollYProgress } = useScroll();
  const heroY = useTransform(scrollYProgress, [0, 0.3], [0, 150]);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const bgAnimation = {
    hidden: { scale: 1.1, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: { duration: 2.5, ease: [0.22, 1, 0.36, 1] },
    },
  };

  const textAnimation = {
    hidden: { y: 30, opacity: 0 },
    visible: (custom) => ({
      y: 0,
      opacity: 1,
      transition: { delay: custom * 0.5 + 1, duration: 1, ease: 'easeOut' },
    }),
  };

  const rooms = [
    {
      name: 'Deluxe Ocean View',
      nameKo: '디럭스 오션뷰',
      price: '350,000',
      size: '45m\u00B2',
      desc: '탁 트인 바다 전망과 함께 프리미엄 침구, 레인샤워, 미니바를 갖춘 럭셔리 객실입니다.',
      features: ['Ocean View', 'King Bed', 'Rain Shower'],
      image: resort5,
    },
    {
      name: 'Premium Suite',
      nameKo: '프리미엄 스위트',
      price: '580,000',
      size: '72m\u00B2',
      desc: '거실과 침실이 분리된 넓은 공간에서 최상의 휴식을 경험하세요. 전용 라운지 이용이 가능합니다.',
      features: ['Garden View', 'Separate Living', 'Executive Lounge'],
      image: resort6,
    },
    {
      name: 'Royal Penthouse',
      nameKo: '로열 펜트하우스',
      price: '1,200,000',
      size: '120m\u00B2',
      desc: '최상층에 위치한 최고급 객실로, 프라이빗 테라스와 자쿠지, 개인 버틀러 서비스를 제공합니다.',
      features: ['Private Terrace', 'Jacuzzi', 'Butler Service'],
      image: resort8,
    },
  ];

  const amenities = [
    { title: 'Infinity Pool', desc: '끝없는 수평선과 하나 되는 인피니티 풀에서 여유로운 시간을 보내세요.' },
    { title: 'Private Spa', desc: '전문 테라피스트의 맞춤 트리트먼트로 몸과 마음의 힐링을 경험하세요.' },
    { title: 'Fine Dining', desc: '미쉐린 셰프가 선보이는 최고급 로컬 & 인터내셔널 요리를 만나보세요.' },
    { title: 'Fitness Center', desc: '최신 장비를 갖춘 피트니스 센터와 개인 트레이닝 프로그램을 이용하세요.' },
    { title: 'Leisure Sports', desc: '테니스, 골프, 요트 등 다양한 레저 스포츠를 즐길 수 있습니다.' },
    { title: 'Kids Club', desc: '전문 스태프가 운영하는 키즈 프로그램으로 가족 모두가 행복한 휴가를 보내세요.' },
  ];

  const dining = [
    {
      name: 'La Maison',
      type: 'French Fine Dining',
      desc: '미쉐린 스타 셰프가 이끄는 프렌치 레스토랑. 제철 식재료로 만든 코스 요리를 즐기세요.',
      hours: 'Dinner 18:00 - 22:00',
    },
    {
      name: 'Umi',
      type: 'Japanese Omakase',
      desc: '도쿄에서 직송되는 신선한 해산물로 만드는 정통 오마카세를 경험하세요.',
      hours: 'Lunch 12:00 - 14:00 | Dinner 18:00 - 21:00',
    },
    {
      name: 'Garden Terrace',
      type: 'All Day Dining',
      desc: '탁 트인 가든 뷰와 함께 세계 각국의 조식 뷔페와 브런치를 즐기세요.',
      hours: 'Breakfast 07:00 - 10:30 | Brunch 11:00 - 15:00',
    },
  ];

  const reviews = [
    {
      name: '김지영',
      rating: 5,
      text: '정말 꿈같은 시간이었어요. 특히 스파 서비스와 오션뷰 객실이 최고였습니다. 다음에도 꼭 다시 오고 싶어요!',
      date: '2025.12',
      room: 'Deluxe Ocean View',
    },
    {
      name: '이민수',
      rating: 5,
      text: '가족 여행으로 방문했는데 키즈 클럽 프로그램이 정말 훌륭했어요. 아이도 부모도 모두 만족한 휴가였습니다.',
      date: '2025.11',
      room: 'Premium Suite',
    },
    {
      name: '박서연',
      rating: 4,
      text: '로열 펜트하우스의 프라이빗 테라스에서 본 일출은 평생 잊지 못할 것 같아요. 서비스도 완벽했습니다.',
      date: '2026.01',
      room: 'Royal Penthouse',
    },
  ];

  const packages = [
    {
      name: 'Honeymoon Package',
      nameKo: '허니문 패키지',
      price: '1,890,000',
      duration: '2 Nights 3 Days',
      includes: ['Premium Suite 2박', 'Couple Spa Treatment', 'Candlelight Dinner', 'Welcome Champagne & Fruits'],
      badge: 'BEST',
      image: resort1,
    },
    {
      name: 'Family Package',
      nameKo: '패밀리 패키지',
      price: '1,450,000',
      duration: '2 Nights 3 Days',
      includes: ['Deluxe Family Room 2박', 'Breakfast Buffet 포함', 'Kids Club 무료 이용', 'Water Activity 1회'],
      badge: '',
      image: resort4,
    },
    {
      name: 'Wellness Retreat',
      nameKo: '웰니스 리트릿',
      price: '2,350,000',
      duration: '3 Nights 4 Days',
      includes: ['Premium Suite 3박', 'Daily Spa Program', 'Yoga & Meditation Class', 'Detox Meal Plan 제공'],
      badge: 'NEW',
      image: resort2,
    },
  ];

  const galleryImages = [
    { src: resort1, alt: 'Sunset gazebo with infinity pool' },
    { src: resort2, alt: 'Tropical pool with palm leaves' },
    { src: resort3, alt: 'Beach sunset with palm tree' },
    { src: resort4, alt: 'Grand resort pool' },
    { src: resort5, alt: 'Overwater villa at sunset' },
    { src: resort6, alt: 'Poolside with parasols' },
    { src: resort7, alt: 'Night pool and restaurant' },
    { src: resort8, alt: 'Poolside lounger and cabana' },
  ];

  const awards = [
    { name: "Cond\u00E9 Nast Traveler", title: "Gold List 2025", detail: "Best Resort in Asia Pacific" },
    { name: "Travel + Leisure", title: "World's Best 2025", detail: "Top 10 Resort Hotels" },
    { name: "Forbes Travel Guide", title: "Five-Star Award", detail: "3 Consecutive Years" },
    { name: "TripAdvisor", title: "Travelers' Choice", detail: "Best of the Best 2025" },
  ];

  return (
    <div className="relative min-h-screen font-sans antialiased bg-stone-50 text-stone-900 overflow-hidden">

      {/* ───────────── Scroll to Top ───────────── */}
      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            onClick={scrollToTop}
            className="fixed bottom-8 right-8 z-50 w-12 h-12 bg-stone-950 text-white rounded-full shadow-lg flex items-center justify-center hover:bg-amber-500 transition-colors duration-300"
            aria-label="Scroll to top"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 15l-6-6-6 6" />
            </svg>
          </motion.button>
        )}
      </AnimatePresence>

      {/* ───────────── Gallery Lightbox ───────────── */}
      <AnimatePresence>
        {galleryOpen !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-stone-950/95 flex items-center justify-center p-4 md:p-12"
            onClick={() => setGalleryOpen(null)}
          >
            <button
              className="absolute top-6 right-6 text-white/60 hover:text-white text-3xl transition"
              onClick={() => setGalleryOpen(null)}
              aria-label="Close"
            >
              {'\u2715'}
            </button>
            <button
              className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 text-white/40 hover:text-white text-4xl transition"
              onClick={(e) => { e.stopPropagation(); setGalleryOpen((galleryOpen - 1 + galleryImages.length) % galleryImages.length); }}
              aria-label="Previous"
            >
              {'\u2039'}
            </button>
            <button
              className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 text-white/40 hover:text-white text-4xl transition"
              onClick={(e) => { e.stopPropagation(); setGalleryOpen((galleryOpen + 1) % galleryImages.length); }}
              aria-label="Next"
            >
              {'\u203A'}
            </button>
            <motion.img
              key={galleryOpen}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              src={galleryImages[galleryOpen].src}
              alt={galleryImages[galleryOpen].alt}
              className="max-h-[85vh] max-w-full object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="absolute bottom-6 text-white/40 text-xs tracking-widest">
              {galleryOpen + 1} / {galleryImages.length}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ───────────── Hero ───────────── */}
      <div className="relative h-screen w-full flex items-center justify-center overflow-hidden">
        <motion.div
          className="absolute inset-0 bg-cover bg-center z-0"
          style={{ backgroundImage: `url(${heroBg})`, y: heroY }}
          initial="hidden"
          animate={isLoaded ? 'visible' : 'hidden'}
          variants={bgAnimation}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
        </motion.div>

        {/* Navigation */}
        <header className={`fixed top-0 left-0 w-full z-50 flex items-center justify-between p-6 md:px-12 text-white transition-all duration-500 ${scrolled ? 'bg-stone-950/90 backdrop-blur-md py-4' : 'bg-transparent'}`}>
          <a href="#" className="text-2xl font-light tracking-widest uppercase">
            Aethelgard <span className="font-bold">Resort</span>
          </a>
          <button
            className="md:hidden text-white text-2xl"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Menu"
          >
            {mobileMenuOpen ? '\u2715' : '\u2630'}
          </button>
          <nav className="hidden md:flex items-center space-x-8 text-sm uppercase tracking-wider font-medium">
            <a href="#about" className="hover:text-amber-300 transition">About</a>
            <a href="#rooms" className="hover:text-amber-300 transition">Rooms</a>
            <a href="#amenities" className="hover:text-amber-300 transition">Amenities</a>
            <a href="#dining" className="hover:text-amber-300 transition">Dining</a>
            <a href="#packages" className="hover:text-amber-300 transition">Packages</a>
            <a href="#book" className="bg-amber-400 text-stone-950 px-6 py-2.5 rounded-full hover:bg-amber-300 transition font-bold">Reserve</a>
          </nav>
        </header>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="fixed top-16 left-0 w-full z-40 bg-stone-950/95 backdrop-blur-md text-white p-8 flex flex-col space-y-5 text-center md:hidden"
          >
            {['About', 'Rooms', 'Amenities', 'Dining', 'Packages'].map((item) => (
              <a key={item} href={`#${item.toLowerCase()}`} onClick={() => setMobileMenuOpen(false)} className="py-2 text-lg tracking-wider hover:text-amber-300 transition">{item}</a>
            ))}
            <a href="#book" onClick={() => setMobileMenuOpen(false)} className="bg-amber-400 text-stone-950 px-6 py-3 rounded-full mx-auto font-bold hover:bg-amber-300 transition">Reserve</a>
          </motion.div>
        )}

        {/* Hero Content */}
        <div className="relative z-10 text-center text-white px-4">
          <motion.p
            custom={0}
            initial="hidden"
            animate={isLoaded ? 'visible' : 'hidden'}
            variants={textAnimation}
            className="text-sm md:text-base uppercase tracking-[0.3em] font-light mb-6 text-white/70"
          >
            The Ultimate Luxury Retreat
          </motion.p>
          <motion.h1
            custom={1}
            initial="hidden"
            animate={isLoaded ? 'visible' : 'hidden'}
            variants={textAnimation}
            className="text-4xl md:text-7xl font-extrabold leading-tight tracking-tight max-w-4xl mx-auto"
          >
            Where Serenity Meets <span className="text-amber-300">Perfection</span>
          </motion.h1>
          <motion.p
            custom={1.5}
            initial="hidden"
            animate={isLoaded ? 'visible' : 'hidden'}
            variants={textAnimation}
            className="mt-6 text-lg md:text-xl text-white/70 max-w-2xl mx-auto font-light leading-relaxed"
          >
            자연과 럭셔리가 조화를 이루는 프리미엄 리조트에서
            <br className="hidden md:block" />
            잊지 못할 순간을 만들어보세요
          </motion.p>
          <motion.div
            custom={2}
            initial="hidden"
            animate={isLoaded ? 'visible' : 'hidden'}
            variants={textAnimation}
            className="mt-12 flex flex-col sm:flex-row gap-4 justify-center"
          >
            <a
              href="#rooms"
              className="inline-block bg-amber-400 text-stone-950 px-10 py-4 rounded-full text-base font-bold tracking-wider hover:bg-amber-300 transition-all duration-300"
            >
              Explore Rooms
            </a>
            <a
              href="#about"
              className="inline-block bg-transparent border-2 border-white/60 text-white px-10 py-4 rounded-full text-base font-bold tracking-wider hover:bg-white hover:text-stone-950 transition-all duration-300"
            >
              Discover More
            </a>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 3.5, duration: 1 }}
          className="absolute bottom-10 z-10 text-white/40 text-xs flex flex-col items-center tracking-widest uppercase"
        >
          <span>Scroll</span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
            className="mt-3 w-px h-8 bg-white/30"
          />
        </motion.div>
      </div>

      {/* ───────────── About ───────────── */}
      <section id="about" className="py-28 px-6 md:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <FadeInSection>
            <p className="text-amber-600 text-xs uppercase tracking-[0.3em] font-semibold text-center mb-4">About Us</p>
            <h2 className="text-3xl md:text-5xl font-bold text-center mb-6 text-stone-950">
              특별한 경험, 잊지 못할 추억
            </h2>
            <div className="w-16 h-px bg-amber-400 mx-auto mb-8"></div>
            <p className="text-stone-500 text-center max-w-3xl mx-auto text-lg leading-relaxed mb-20">
              Aethelgard Resort는 2005년 개관 이래, 자연의 아름다움과 최고급 서비스를 결합하여
              전 세계 여행자들에게 잊지 못할 경험을 선사하고 있습니다.
            </p>
          </FadeInSection>

          <div className="grid md:grid-cols-2 gap-6 mb-20">
            <FadeInSection delay={0.1}>
              <div className="rounded-2xl overflow-hidden h-72 md:h-[28rem]">
                <img src={resort3} alt="Beachside sunset" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              </div>
            </FadeInSection>
            <FadeInSection delay={0.25}>
              <div className="rounded-2xl overflow-hidden h-72 md:h-[28rem]">
                <img src={resort4} alt="Resort main pool" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              </div>
            </FadeInSection>
          </div>

          <div className="grid md:grid-cols-3 gap-8 text-center">
            {[
              { value: '20+', label: 'Years of Excellence' },
              { value: '98%', label: 'Guest Satisfaction' },
              { value: '5-Star', label: 'Premium Rating' },
            ].map((stat, i) => (
              <FadeInSection key={stat.label} delay={i * 0.1}>
                <div className="p-8">
                  <div className="text-5xl md:text-6xl font-bold text-amber-500 mb-3">{stat.value}</div>
                  <p className="text-stone-500 font-medium tracking-wide text-sm uppercase">{stat.label}</p>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── Awards & Recognition ───────────── */}
      <section className="py-16 px-6 md:px-12 bg-stone-100 border-y border-stone-200">
        <div className="max-w-7xl mx-auto">
          <FadeInSection>
            <p className="text-stone-400 text-xs uppercase tracking-[0.3em] font-semibold text-center mb-10">Awards & Recognition</p>
          </FadeInSection>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {awards.map((award, i) => (
              <FadeInSection key={award.name} delay={i * 0.08}>
                <div className="text-center p-4">
                  <p className="text-stone-950 font-bold text-sm tracking-wide mb-1">{award.name}</p>
                  <p className="text-amber-600 text-xs font-semibold uppercase tracking-wider mb-1">{award.title}</p>
                  <p className="text-stone-400 text-xs">{award.detail}</p>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── Rooms ───────────── */}
      <section id="rooms" className="py-28 px-6 md:px-12 bg-stone-50">
        <div className="max-w-7xl mx-auto">
          <FadeInSection>
            <p className="text-amber-600 text-xs uppercase tracking-[0.3em] font-semibold text-center mb-4">Rooms & Suites</p>
            <h2 className="text-3xl md:text-5xl font-bold text-center mb-4 text-stone-950">프리미엄 객실</h2>
            <div className="w-16 h-px bg-amber-400 mx-auto mb-6"></div>
            <p className="text-stone-500 text-center max-w-2xl mx-auto mb-20">
              모든 객실은 최고급 인테리어와 최신 편의시설을 갖추고 있으며, 탁 트인 전망을 자랑합니다.
            </p>
          </FadeInSection>
          <div className="grid md:grid-cols-3 gap-8">
            {rooms.map((room, i) => (
              <FadeInSection key={room.name} delay={i * 0.15}>
                <div className="group bg-white rounded-2xl shadow-md overflow-hidden hover:shadow-2xl transition-all duration-500 flex flex-col h-full">
                  <div className="h-60 overflow-hidden relative">
                    <img
                      src={room.image}
                      alt={room.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full">
                      <span className="text-xs font-semibold text-stone-700">{room.size}</span>
                    </div>
                  </div>
                  <div className="p-8 flex flex-col flex-1">
                    <h3 className="text-xl font-bold text-stone-950 tracking-wide">{room.name}</h3>
                    <p className="text-sm text-stone-400 mb-4">{room.nameKo}</p>
                    <p className="text-stone-500 leading-relaxed mb-6 flex-1 text-sm">{room.desc}</p>
                    <div className="flex flex-wrap gap-2 mb-6">
                      {room.features.map((f) => (
                        <span key={f} className="text-xs bg-stone-100 text-stone-600 px-3 py-1.5 rounded-full font-medium">{f}</span>
                      ))}
                    </div>
                    <div className="flex items-end justify-between mt-auto pt-6 border-t border-stone-100">
                      <div>
                        <span className="text-sm text-stone-400">From</span>
                        <div>
                          <span className="text-2xl font-bold text-stone-950">{'\u20A9'}{room.price}</span>
                          <span className="text-stone-400 text-sm"> / night</span>
                        </div>
                      </div>
                      <a href="#book" className="bg-stone-950 text-white px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-amber-500 transition-colors duration-300">
                        Book
                      </a>
                    </div>
                  </div>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── Amenities ───────────── */}
      <section id="amenities" className="py-28 px-6 md:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <FadeInSection>
            <p className="text-amber-600 text-xs uppercase tracking-[0.3em] font-semibold text-center mb-4">Amenities</p>
            <h2 className="text-3xl md:text-5xl font-bold text-center mb-4 text-stone-950">최고급 부대시설</h2>
            <div className="w-16 h-px bg-amber-400 mx-auto mb-6"></div>
            <p className="text-stone-500 text-center max-w-2xl mx-auto mb-20">
              투숙객만을 위한 프리미엄 시설과 서비스를 경험하세요.
            </p>
          </FadeInSection>

          <FadeInSection>
            <div className="rounded-2xl overflow-hidden mb-16 h-64 md:h-[28rem]">
              <img src={resort2} alt="Resort pool" className="w-full h-full object-cover" />
            </div>
          </FadeInSection>

          <div className="grid md:grid-cols-3 gap-6">
            {amenities.map((item, i) => (
              <FadeInSection key={item.title} delay={i * 0.08}>
                <div className="group p-8 rounded-2xl border border-stone-200 hover:border-amber-300 hover:shadow-lg transition-all duration-300 bg-white">
                  <h3 className="text-lg font-bold mb-3 text-stone-950 group-hover:text-amber-600 transition-colors">{item.title}</h3>
                  <p className="text-stone-500 leading-relaxed text-sm">{item.desc}</p>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── Dining ───────────── */}
      <section id="dining" className="relative py-28 px-6 md:px-12 text-white overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={resort7} alt="Dining atmosphere" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-stone-950/85 via-stone-950/75 to-stone-950/90"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto">
          <FadeInSection>
            <p className="text-amber-400 text-xs uppercase tracking-[0.3em] font-semibold text-center mb-4">Dining</p>
            <h2 className="text-3xl md:text-5xl font-bold text-center mb-4">미식의 세계로 초대합니다</h2>
            <div className="w-16 h-px bg-amber-400 mx-auto mb-6"></div>
            <p className="text-stone-400 text-center max-w-2xl mx-auto mb-20">
              세계 각국의 셰프들이 선보이는 특별한 미식 경험을 즐겨보세요.
            </p>
          </FadeInSection>
          <div className="grid md:grid-cols-3 gap-8">
            {dining.map((restaurant, i) => (
              <FadeInSection key={restaurant.name} delay={i * 0.15}>
                <div className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl border border-white/10 hover:border-amber-400/40 hover:bg-white/15 transition-all duration-300">
                  <span className="text-amber-400 text-xs uppercase tracking-[0.2em] font-semibold">{restaurant.type}</span>
                  <h3 className="text-2xl font-bold mt-3 mb-4">{restaurant.name}</h3>
                  <p className="text-stone-300 leading-relaxed mb-8 text-sm">{restaurant.desc}</p>
                  <div className="text-xs text-stone-400 border-t border-white/10 pt-4 tracking-wider uppercase">
                    {restaurant.hours}
                  </div>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── Packages ───────────── */}
      <section id="packages" className="py-28 px-6 md:px-12 bg-stone-50">
        <div className="max-w-7xl mx-auto">
          <FadeInSection>
            <p className="text-amber-600 text-xs uppercase tracking-[0.3em] font-semibold text-center mb-4">Special Packages</p>
            <h2 className="text-3xl md:text-5xl font-bold text-center mb-4 text-stone-950">특별 패키지</h2>
            <div className="w-16 h-px bg-amber-400 mx-auto mb-6"></div>
            <p className="text-stone-500 text-center max-w-2xl mx-auto mb-20">
              목적에 맞는 특별 패키지로 더욱 풍성한 여행을 즐기세요.
            </p>
          </FadeInSection>
          <div className="grid md:grid-cols-3 gap-8">
            {packages.map((pkg, i) => (
              <FadeInSection key={pkg.name} delay={i * 0.15}>
                <div className="group relative bg-white rounded-2xl shadow-md overflow-hidden hover:shadow-2xl transition-all duration-500 flex flex-col h-full">
                  {pkg.badge && (
                    <span className="absolute top-4 right-4 z-10 bg-amber-400 text-stone-950 text-xs font-bold px-3 py-1 rounded-full tracking-wider">
                      {pkg.badge}
                    </span>
                  )}
                  <div className="h-52 overflow-hidden">
                    <img
                      src={pkg.image}
                      alt={pkg.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-8 flex flex-col flex-1">
                    <h3 className="text-xl font-bold text-stone-950 tracking-wide">{pkg.name}</h3>
                    <p className="text-sm text-stone-400 mb-1">{pkg.nameKo}</p>
                    <p className="text-xs text-stone-400 mb-6 tracking-wider uppercase">{pkg.duration}</p>
                    <ul className="space-y-3 mb-8 flex-1">
                      {pkg.includes.map((item) => (
                        <li key={item} className="flex items-start text-stone-500 text-sm">
                          <span className="text-amber-500 mr-2.5 mt-0.5 text-xs">{'\u2713'}</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                    <div className="border-t border-stone-100 pt-6 flex items-end justify-between mt-auto">
                      <div>
                        <span className="text-sm text-stone-400">From</span>
                        <div>
                          <span className="text-2xl font-bold text-stone-950">{'\u20A9'}{pkg.price}</span>
                        </div>
                      </div>
                      <a href="#book" className="bg-stone-950 text-white px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-amber-500 transition-colors duration-300">
                        Book
                      </a>
                    </div>
                  </div>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── Gallery ───────────── */}
      <section className="py-28 px-6 md:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <FadeInSection>
            <p className="text-amber-600 text-xs uppercase tracking-[0.3em] font-semibold text-center mb-4">Gallery</p>
            <h2 className="text-3xl md:text-5xl font-bold text-center mb-4 text-stone-950">리조트 갤러리</h2>
            <div className="w-16 h-px bg-amber-400 mx-auto mb-6"></div>
            <p className="text-stone-500 text-center max-w-2xl mx-auto mb-16">
              Aethelgard Resort의 아름다운 순간들을 만나보세요.
            </p>
          </FadeInSection>

          {/* Row 1: 큰 이미지 1개 + 작은 이미지 2개 세로 */}
          <div className="grid md:grid-cols-3 gap-3 mb-3">
            <FadeInSection className="md:col-span-2">
              <button onClick={() => setGalleryOpen(0)} className="block w-full h-64 md:h-[26rem] overflow-hidden rounded-xl cursor-pointer group">
                <img src={galleryImages[0].src} alt={galleryImages[0].alt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </button>
            </FadeInSection>
            <div className="flex flex-col gap-3">
              <FadeInSection delay={0.1}>
                <button onClick={() => setGalleryOpen(1)} className="block w-full h-32 md:h-[12.75rem] overflow-hidden rounded-xl cursor-pointer group">
                  <img src={galleryImages[1].src} alt={galleryImages[1].alt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </button>
              </FadeInSection>
              <FadeInSection delay={0.15}>
                <button onClick={() => setGalleryOpen(2)} className="block w-full h-32 md:h-[12.75rem] overflow-hidden rounded-xl cursor-pointer group">
                  <img src={galleryImages[2].src} alt={galleryImages[2].alt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </button>
              </FadeInSection>
            </div>
          </div>

          {/* Row 2: 4개 균등 */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            {galleryImages.slice(3, 7).map((img, i) => (
              <FadeInSection key={i + 3} delay={i * 0.08}>
                <button onClick={() => setGalleryOpen(i + 3)} className="block w-full h-40 md:h-52 overflow-hidden rounded-xl cursor-pointer group">
                  <img src={img.src} alt={img.alt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </button>
              </FadeInSection>
            ))}
          </div>

          {/* Row 3: 마지막 와이드 이미지 */}
          <FadeInSection delay={0.1}>
            <button onClick={() => setGalleryOpen(7)} className="block w-full h-48 md:h-72 overflow-hidden rounded-xl cursor-pointer group">
              <img src={galleryImages[7].src} alt={galleryImages[7].alt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            </button>
          </FadeInSection>
        </div>
      </section>

      {/* ───────────── Reviews ───────────── */}
      <section className="py-28 px-6 md:px-12 bg-stone-50">
        <div className="max-w-6xl mx-auto">
          <FadeInSection>
            <p className="text-amber-600 text-xs uppercase tracking-[0.3em] font-semibold text-center mb-4">Guest Reviews</p>
            <h2 className="text-3xl md:text-5xl font-bold text-center mb-4 text-stone-950">고객 후기</h2>
            <div className="w-16 h-px bg-amber-400 mx-auto mb-6"></div>
            <p className="text-stone-500 text-center max-w-2xl mx-auto mb-20">
              Aethelgard Resort를 경험한 고객님들의 솔직한 후기입니다.
            </p>
          </FadeInSection>
          <div className="grid md:grid-cols-3 gap-8">
            {reviews.map((review, i) => (
              <FadeInSection key={review.name} delay={i * 0.15}>
                <div className="bg-white p-8 rounded-2xl border border-stone-100 shadow-sm flex flex-col h-full">
                  <div className="flex items-center mb-5">
                    <div className="w-11 h-11 bg-stone-900 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                      {review.name[0]}
                    </div>
                    <div className="ml-3.5">
                      <p className="font-semibold text-stone-950 text-sm">{review.name}</p>
                      <p className="text-xs text-stone-400">{review.room} &middot; {review.date}</p>
                    </div>
                  </div>
                  <div className="text-amber-400 mb-4 text-sm tracking-wider">
                    {'\u2605'.repeat(review.rating)}{'\u2606'.repeat(5 - review.rating)}
                  </div>
                  <p className="text-stone-500 leading-relaxed text-sm flex-1">"{review.text}"</p>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── Reservation CTA with Booking Widget ───────────── */}
      <section id="book" className="relative py-28 px-6 md:px-12 text-white overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={resort1} alt="Resort sunset" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-stone-950/85 via-stone-950/70 to-stone-950/85"></div>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto">
          <FadeInSection>
            <div className="text-center mb-14">
              <p className="text-amber-400 text-xs uppercase tracking-[0.3em] font-semibold mb-6">Reservation</p>
              <h2 className="text-3xl md:text-5xl font-bold mb-6 leading-tight">
                지금, 특별한 여행을 시작하세요
              </h2>
              <div className="w-16 h-px bg-amber-400 mx-auto mb-6"></div>
              <p className="text-white/50 text-base">
                온라인 예약 시 Early Bird 10% 할인과 조식을 제공합니다.
              </p>
            </div>

            {/* Booking Form */}
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 md:p-10 border border-white/10">
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div>
                  <label className="block text-xs uppercase tracking-wider text-white/50 mb-2 font-medium">Check-in</label>
                  <input
                    type="date"
                    value={bookingForm.checkIn}
                    onChange={(e) => setBookingForm({ ...bookingForm, checkIn: e.target.value })}
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-amber-400 transition text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-white/50 mb-2 font-medium">Check-out</label>
                  <input
                    type="date"
                    value={bookingForm.checkOut}
                    onChange={(e) => setBookingForm({ ...bookingForm, checkOut: e.target.value })}
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-amber-400 transition text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-white/50 mb-2 font-medium">Guests</label>
                  <select
                    value={bookingForm.guests}
                    onChange={(e) => setBookingForm({ ...bookingForm, guests: e.target.value })}
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-amber-400 transition text-sm appearance-none"
                  >
                    <option value="1" className="bg-stone-900">1 Guest</option>
                    <option value="2" className="bg-stone-900">2 Guests</option>
                    <option value="3" className="bg-stone-900">3 Guests</option>
                    <option value="4" className="bg-stone-900">4 Guests</option>
                    <option value="5" className="bg-stone-900">5+ Guests</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-white/50 mb-2 font-medium">Room Type</label>
                  <select
                    value={bookingForm.room}
                    onChange={(e) => setBookingForm({ ...bookingForm, room: e.target.value })}
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-amber-400 transition text-sm appearance-none"
                  >
                    <option value="deluxe" className="bg-stone-900">Deluxe Ocean View</option>
                    <option value="premium" className="bg-stone-900">Premium Suite</option>
                    <option value="penthouse" className="bg-stone-900">Royal Penthouse</option>
                  </select>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                <p className="text-white/30 text-xs tracking-widest uppercase">Check-in 15:00 &middot; Check-out 11:00</p>
                <button className="bg-amber-400 text-stone-950 px-10 py-4 rounded-full text-base font-bold tracking-wider hover:bg-amber-300 transition-all duration-300 w-full sm:w-auto">
                  Check Availability
                </button>
              </div>
            </div>

            <div className="text-center mt-8">
              <p className="text-white/40 text-sm mb-2">전화 예약도 가능합니다</p>
              <a
                href="tel:+82-2-1234-5678"
                className="text-white/70 hover:text-amber-400 transition text-lg font-light tracking-wider"
              >
                Tel. 02-1234-5678
              </a>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* ───────────── Location ───────────── */}
      <section className="py-28 px-6 md:px-12 bg-white">
        <div className="max-w-6xl mx-auto">
          <FadeInSection>
            <p className="text-amber-600 text-xs uppercase tracking-[0.3em] font-semibold text-center mb-4">Location</p>
            <h2 className="text-3xl md:text-5xl font-bold text-center mb-4 text-stone-950">오시는 길</h2>
            <div className="w-16 h-px bg-amber-400 mx-auto mb-16"></div>
          </FadeInSection>
          <div className="grid md:grid-cols-3 gap-8 text-center">
            {[
              { icon: 'CAR', title: '자가용', desc: '서울에서 약 2시간 30분\n제주공항에서 약 40분' },
              { icon: 'AIR', title: '항공편', desc: '제주국제공항 도착 후\n무료 셔틀버스 운행' },
              { icon: 'BUS', title: 'Shuttle Service', desc: '공항 — 리조트\n매일 08:00 - 22:00 운행' },
            ].map((item, i) => (
              <FadeInSection key={item.icon} delay={i * 0.1}>
                <div className="p-8">
                  <div className="w-16 h-16 border-2 border-stone-200 rounded-full flex items-center justify-center mx-auto mb-6">
                    <span className="text-stone-400 text-xs font-bold tracking-widest">{item.icon}</span>
                  </div>
                  <h3 className="font-bold text-stone-950 mb-3">{item.title}</h3>
                  <p className="text-stone-500 text-sm leading-relaxed whitespace-pre-line">{item.desc}</p>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── Footer ───────────── */}
      <footer className="py-20 bg-stone-950 text-stone-400 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-16">
            <div>
              <a href="#" className="text-xl text-white font-light tracking-widest uppercase block mb-5">
                Aethelgard <span className="font-bold">Resort</span>
              </a>
              <p className="text-sm leading-relaxed text-stone-500">
                Where nature meets luxury.
                <br />
                A 5-star premium resort.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-5 text-sm tracking-wider uppercase">Quick Links</h4>
              <ul className="space-y-3 text-sm">
                <li><a href="#about" className="hover:text-amber-400 transition">About</a></li>
                <li><a href="#rooms" className="hover:text-amber-400 transition">Rooms & Suites</a></li>
                <li><a href="#dining" className="hover:text-amber-400 transition">Dining</a></li>
                <li><a href="#packages" className="hover:text-amber-400 transition">Packages</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-5 text-sm tracking-wider uppercase">Guest Services</h4>
              <ul className="space-y-3 text-sm">
                <li><a href="#book" className="hover:text-amber-400 transition">Reservation</a></li>
                <li><a href="#" className="hover:text-amber-400 transition">FAQ</a></li>
                <li><a href="#" className="hover:text-amber-400 transition">Terms & Conditions</a></li>
                <li><a href="#" className="hover:text-amber-400 transition">Privacy Policy</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-5 text-sm tracking-wider uppercase">Contact</h4>
              <ul className="space-y-3 text-sm">
                <li>Tel. 02-1234-5678</li>
                <li>info@aethelgard.kr</li>
                <li className="leading-relaxed">
                  제주특별자치도 서귀포시
                  <br />중문관광로 123
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-stone-800/50 pt-8 flex flex-col md:flex-row justify-between items-center text-xs text-stone-600">
            <p>&copy; 2026 Aethelgard Resort. All rights reserved.</p>
            <div className="flex space-x-6 mt-4 md:mt-0 tracking-wider uppercase">
              <a href="#" className="hover:text-amber-400 transition">Instagram</a>
              <a href="#" className="hover:text-amber-400 transition">Facebook</a>
              <a href="#" className="hover:text-amber-400 transition">YouTube</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
